import { useState, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { reverseGeocode } from '../services/api';
import { parseWKTGeometry, getGeometryCoordinates } from '../utils/helpers';

const RVG_MASK_BOUNDS_RING = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]];

/**
 * Custom hook for managing reverse geocoding functionality
 */
export const useReverseGeocode = (mapRef) => {
  const [rvgResults, setRvgResults] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [clickedPoint, setClickedPoint] = useState(null);
  const [rvgLoading, setRvgLoading] = useState(false);
  const [highlightedResultIndex, setHighlightedResultIndex] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);

  const markersRef = useRef([]);
  const popupMarkerRef = useRef(null);

  const buildInverseMaskFeature = useCallback((shapeGeometry) => {
    if (!shapeGeometry || !shapeGeometry.coordinates) return null;

    let holeRings = [];
    if (shapeGeometry.type === 'Polygon') {
      holeRings = shapeGeometry.coordinates;
    } else if (shapeGeometry.type === 'MultiPolygon') {
      holeRings = shapeGeometry.coordinates.flat();
    }

    if (!holeRings.length) return null;

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [RVG_MASK_BOUNDS_RING, ...holeRings]
      }
    };
  }, []);

  // Function to call reverse geocode API and display results
  const callReverseGeocode = useCallback(async (lat, lng, clickedCoords = null, showMarkerOnly = false, skipCentering = false) => {
    // Format coordinates with 6 decimal places precision
    const formattedLat = parseFloat(lat).toFixed(6);
    const formattedLng = parseFloat(lng).toFixed(6);

    // Get current zoom level from map
    const currentZoom = mapRef.current ? mapRef.current.getZoom() : 14;

    if (!showMarkerOnly) {
      // Show sidebar and loading state (old behavior)
      setRvgLoading(true);
      setShowSidebar(true);
      setIsClosing(false);
      setHighlightedResultIndex(null);
      setClickedPoint(clickedCoords || { lat, lng });
    }

    try {
      const response = await reverseGeocode(formattedLat, formattedLng, currentZoom);

      // Handle new API format: response.data contains the actual data
      const data = response?.data || response;

      // Check if response is valid
      if (!data || (!data.entity_name && !data.display_name && !data.name)) {
        if (!showMarkerOnly) {
          setRvgResults([]);
          setRvgLoading(false);
          setShowSidebar(false);
        }
        return;
      }

      if (showMarkerOnly) {
        // New behavior: Just show marker and bottom card, no sidebar
        // Remove all old markers
        markersRef.current.forEach((marker) => {
          if (marker) marker.remove();
        });
        markersRef.current = [];

        // Remove existing shape layer if any
        if (mapRef.current.getLayer('rvg-shape-fill')) {
          mapRef.current.removeLayer('rvg-shape-fill');
        }
        if (mapRef.current.getLayer('rvg-shape-outside')) {
          mapRef.current.removeLayer('rvg-shape-outside');
        }
        if (mapRef.current.getLayer('rvg-shape-outline')) {
          mapRef.current.removeLayer('rvg-shape-outline');
        }
        if (mapRef.current.getSource('rvg-shape')) {
          mapRef.current.removeSource('rvg-shape');
        }
        if (mapRef.current.getSource('rvg-shape-mask')) {
          mapRef.current.removeSource('rvg-shape-mask');
        }

        // Check if shape exists in response (support both old and new formats)
        let shapeGeometry = null;

        // Newest format: shape field with WKT string
        if (data.shape && typeof data.shape === 'string') {
          shapeGeometry = parseWKTGeometry(data.shape);
        }
        // Previous format: geometry field with WKT string
        else if (data.geometry && typeof data.geometry === 'string') {
          shapeGeometry = parseWKTGeometry(data.geometry);
        }
        // Old format: shape.coordinates
        else if (data.shape && data.shape.coordinates) {
          const isMultiPolygon = Array.isArray(data.shape.coordinates?.[0]?.[0]?.[0]);
          shapeGeometry = {
            type: data.shape.type || (isMultiPolygon ? 'MultiPolygon' : 'Polygon'),
            coordinates: data.shape.coordinates
          };
        }

        if (shapeGeometry) {
          // Add shape as a polygon layer
          mapRef.current.addSource('rvg-shape', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: shapeGeometry.type,
                coordinates: shapeGeometry.coordinates
              }
            }
          });

          const inverseMaskFeature = buildInverseMaskFeature(shapeGeometry);
          if (inverseMaskFeature) {
            mapRef.current.addSource('rvg-shape-mask', {
              type: 'geojson',
              data: inverseMaskFeature
            });

            // Darken only the outside area by cutting the shape from a world mask.
            mapRef.current.addLayer({
              id: 'rvg-shape-outside',
              type: 'fill',
              source: 'rvg-shape-mask',
              paint: {
                'fill-color': '#000000',
                'fill-opacity': 0.28
              }
            });
          }

          // Keep inside area light for better contrast.
          mapRef.current.addLayer({
            id: 'rvg-shape-fill',
            type: 'fill',
            source: 'rvg-shape',
            paint: {
              'fill-color': '#ffffff',
              'fill-opacity': 0.08
            }
          });

          // Add outline layer with dashed line
          mapRef.current.addLayer({
            id: 'rvg-shape-outline',
            type: 'line',
            source: 'rvg-shape',
            paint: {
              'line-color': '#dc3545',
              'line-width': 2
            }
          });

          // Calculate bounds and fit map to show entire shape
          const coordinates = getGeometryCoordinates(shapeGeometry);
          if (coordinates.length > 0) {
            const bounds = coordinates.reduce((bounds, coord) => {
              return bounds.extend(coord);
            }, new maplibregl.LngLatBounds(coordinates[0], coordinates[0]));

            mapRef.current.fitBounds(bounds, {
              padding: 50,
              duration: 1000,
              maxZoom: 17
            });
          }
        }

        const responseType = String(data.type || data.entity_type || '').toLowerCase();
        const responseLat = parseFloat(data.lat ?? data.location?.lat);
        const responseLng = parseFloat(data.long ?? data.lon ?? data.location?.long);
        const clickedLat = parseFloat(lat);
        const clickedLng = parseFloat(lng);
        const useResponseCoords = responseType === 'building' && !isNaN(responseLat) && !isNaN(responseLng);
        const pinLat = useResponseCoords ? responseLat : clickedLat;
        const pinLng = useResponseCoords ? responseLng : clickedLng;

        // Only adjust the view if the pin is covered by the RVG bottom card
        // Skip adjustment when polygon exists because fitBounds already adjusts the view
        if (mapRef.current && !skipCentering && !shapeGeometry) {
          const map = mapRef.current;
          const maxAttempts = 5;
          let attempts = 0;

          const tryAdjustForCard = () => {
            attempts += 1;
            const cardEl = document.querySelector('.rvg-bottom-card');
            const canvas = map.getCanvas && map.getCanvas();

            if (!cardEl || !canvas) {
              if (attempts < maxAttempts) {
                requestAnimationFrame(tryAdjustForCard);
              }
              return;
            }

            const canvasRect = canvas.getBoundingClientRect();
            const cardRect = cardEl.getBoundingClientRect();
            const projected = map.project([pinLng, pinLat]);
            const pinViewportX = canvasRect.left + projected.x;
            const pinViewportY = canvasRect.top + projected.y;
            const isCoveredByCard =
              pinViewportX >= cardRect.left &&
              pinViewportX <= cardRect.right &&
              pinViewportY >= cardRect.top &&
              pinViewportY <= cardRect.bottom;

            if (isCoveredByCard) {
              const visibleTop = canvasRect.top;
              const visibleBottom = Math.min(cardRect.top, canvasRect.bottom);
              const visibleHeight = Math.max(0, visibleBottom - visibleTop);
              const targetViewportX = canvasRect.left + canvasRect.width / 2;
              const targetViewportY = visibleTop + visibleHeight / 2;
              const deltaViewportX = pinViewportX - targetViewportX;
              const deltaViewportY = pinViewportY - targetViewportY;
              if (Math.abs(deltaViewportX) > 1 || Math.abs(deltaViewportY) > 1) {
                map.panBy([deltaViewportX, deltaViewportY], { duration: 1000 });
              }
            }
          };

          requestAnimationFrame(tryAdjustForCard);
        }
        // Create/update the RVG pin marker at the resolved location
        const popupLng = pinLng;
        const popupLat = pinLat;
        if (!isNaN(popupLng) && !isNaN(popupLat) && mapRef.current) {
          if (popupMarkerRef.current) {
            popupMarkerRef.current.remove();
            popupMarkerRef.current = null;
          }
          const markerEl = document.createElement('div');
          markerEl.className = 'rvg-pin-marker';
          popupMarkerRef.current = new maplibregl.Marker({
            element: markerEl,
            anchor: 'bottom'
          })
            .setLngLat([popupLng, popupLat])
            .addTo(mapRef.current);

          // Ensure RVG pin renders above POI icons
          const rvgWrapperEl = popupMarkerRef.current.getElement();
          if (rvgWrapperEl) rvgWrapperEl.style.zIndex = '2000';
        }

        // Store the RVG data with clicked coordinates to show in bottom card
        // Normalize the new API format to match expected structure
        const normalizedData = {
          entity_id: data.entity_id ?? data.id ?? null,
          entity_name: data.entity_name || data.display_name || data.name,
          entity_type: data.entity_type || data.type || null,
          name: data.entity_name || data.display_name || data.name,
          display_name: data.entity_name || data.display_name || data.name,
          rooftop_name: data.entity_name || data.display_name || data.name,
          display_text: data.display_text || data.entity_address || data.display_address || '',
          display_address: data.display_text || data.entity_address || data.display_address || '',
          full_address: data.full_address || data.entity_address || data.display_address || '',
          type: data.entity_type || data.type || 'location',
          score: data.score ?? null,
          distance_m: data.distance_m || 0,
          source: 'REVERSE_GEOCODE',
          location: {
            lat: pinLat,
            lon: pinLng,
            long: pinLng
          },
          clickedLocation: {
            lat: clickedLat,
            long: clickedLng
          }
        };

        // Set popup data and coordinates atomically
        // Set all state synchronously to ensure they're available together
        setPopupPosition({ lng: popupLng, lat: popupLat });
        setPopupData(normalizedData);
        setShowPopup(true);

        setRvgResults([normalizedData]);
        setShowSidebar(false); // Don't show sidebar
        setRvgLoading(false);
        return;
      }

      // Old behavior: Show sidebar with results
      // Handle both new single-object format and old results array format
      const resultsArray = data.results || [data];

      // Normalize each result to ensure consistent structure
      const normalizedResults = resultsArray.map(result => ({
        name: result.entity_name || result.display_name || result.name,
        rooftop_name: result.entity_name || result.display_name || result.rooftop_name || result.name,
        display_address: result.entity_address || result.display_address || result.address || '',
        type: result.entity_type || result.type || 'location',
        distance_m: result.distance_m || 0,
        location: result.location || {
          lat: result.lat,
          long: result.long || result.lon
        },
        centroid_point: result.centroid_point || result.location || {
          lat: result.lat,
          long: result.long || result.lon
        }
      }));

      setRvgResults(normalizedResults);
      setIsClosing(false);
      setRvgLoading(false);

      // Remove all old markers
      markersRef.current.forEach((marker) => {
        if (marker) marker.remove();
      });
      markersRef.current = [];

      // Add markers for each result
      data.results.forEach((result, index) => {
        // Support both new API format (location) and old format (centroid_point)
        const centroid = result.location || result.centroid_point;
        const color = "#7272db";

        // Check if this is the clicked point
        const currentClickedPoint = clickedCoords || { lat, lng };
        const centroidLat = centroid?.lat;
        const centroidLng = centroid?.long || centroid?.lng;
        const isClickedPoint = currentClickedPoint && centroidLat && centroidLng &&
          Math.abs(centroidLat - currentClickedPoint.lat) < 0.0001 &&
          Math.abs(centroidLng - currentClickedPoint.lng) < 0.0001;

        // Create custom HTML element for marker
        const el = document.createElement("div");
        el.className = "custom-marker-container";

        if (isClickedPoint) {
          el.style.zIndex = "2000";

          const label = document.createElement("div");
          label.className = "custom-marker-label";
          label.textContent = result.rooftop_name;
          label.style.marginBottom = "2px";
          label.style.padding = "2px 6px";
          label.style.backgroundColor = "transparent";
          label.style.borderRadius = "4px";
          label.style.fontSize = "16px";
          label.style.fontWeight = "600";
          label.style.color = "#333";
          label.style.whiteSpace = "nowrap";
          label.style.textShadow = "0 1px 2px rgba(255,255,255,0.8), 0 0 4px rgba(255,255,255,0.5)";
          label.style.textAlign = "center";
          label.style.maxWidth = "200px";
          label.style.overflow = "hidden";
          label.style.textOverflow = "ellipsis";
          label.style.position = "relative";
          label.style.zIndex = "2000";

          const iconElement = document.createElement("div");
          iconElement.innerHTML = `
            <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 0C5.37258 0 0 5.37258 0 12C0 18.6274 12 32 12 32C12 32 24 18.6274 24 12C24 5.37258 18.6274 0 12 0Z" fill="#FF3B30"/>
              <circle cx="12" cy="12" r="5" fill="white"/>
            </svg>
          `;
          iconElement.style.textAlign = "center";
          iconElement.style.filter = "drop-shadow(0 2px 4px rgba(0,0,0,0.3))";
          iconElement.style.lineHeight = "0";
          iconElement.style.margin = "0 auto";
          iconElement.style.position = "relative";
          iconElement.style.zIndex = "2000";

          el.appendChild(label);
          el.appendChild(iconElement);
        } else {
          // Regular marker
          const markerPin = document.createElement("div");
          markerPin.className = "custom-marker-pin";
          markerPin.style.backgroundColor = color;
          markerPin.style.width = "12px";
          markerPin.style.height = "12px";
          markerPin.style.borderRadius = "50%";
          markerPin.style.border = "1.5px solid white";
          markerPin.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
          markerPin.style.margin = "0 auto";
          markerPin.style.cursor = "pointer";
          markerPin.style.transition = "all 0.2s ease";
          markerPin.style.transformOrigin = "center center";

          const label = document.createElement("div");
          label.className = "custom-marker-label marker-hover-label";
          label.textContent = result.rooftop_name;
          label.style.marginBottom = "2px";
          label.style.padding = "4px 8px";
          label.style.backgroundColor = "#fffaf0";
          label.style.borderRadius = "6px";
          label.style.fontSize = "11px";
          label.style.fontWeight = "600";
          label.style.color = "#333";
          label.style.whiteSpace = "nowrap";
          label.style.textAlign = "center";
          label.style.maxWidth = "150px";
          label.style.overflow = "hidden";
          label.style.textOverflow = "ellipsis";
          label.style.opacity = "0";
          label.style.transition = "opacity 0.2s ease";
          label.style.pointerEvents = "none";
          label.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
          label.style.border = "1px solid rgba(0,0,0,0.1)";
          label.style.zIndex = "1000";
          label.style.position = "relative";

          // Add hover effect
          el.addEventListener('mouseenter', () => {
            markerPin.style.transform = "scale(1.33)";
            markerPin.style.boxShadow = "0 2px 6px rgba(0,0,0,0.5)";
            label.style.opacity = "1";
            el.style.zIndex = "1000";
          });
          el.addEventListener('mouseleave', () => {
            markerPin.style.transform = "scale(1)";
            markerPin.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
            label.style.opacity = "0";
            el.style.zIndex = "auto";
          });

          el.appendChild(label);
          el.appendChild(markerPin);
        }

        // Create marker
        const marker = new maplibregl.Marker({
          element: el,
          anchor: isClickedPoint ? 'bottom' : 'center',
          pitchAlignment: 'map',
          rotationAlignment: 'map'
        })
          .setLngLat([centroidLng, centroidLat])
          .addTo(mapRef.current);

        if (!isClickedPoint) {
          // Highlight sidebar result on click
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!showSidebar) {
              setShowSidebar(true);
              setIsClosing(false);
            }
            setHighlightedResultIndex(index);
            setTimeout(() => {
              const resultCard = document.querySelectorAll('.result-card')[index];
              if (resultCard) {
                resultCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }, 100);
          });
        }

        markersRef.current.push(marker);
      });

      // Fit map to show all results
      if (mapRef.current && data.results.length > 0) {
        try {
          const bounds = new maplibregl.LngLatBounds();

          data.results.forEach((result) => {
            const resultLocation = result.location || result.centroid_point;
            const resultLng = resultLocation?.long || resultLocation?.lng;
            const resultLat = resultLocation?.lat;
            if (resultLat && resultLng) {
              bounds.extend([resultLng, resultLat]);
            }
          });

          if (!bounds.isEmpty()) {
            setTimeout(() => {
              if (mapRef.current) {
                mapRef.current.fitBounds(bounds, {
                  padding: { top: 100, bottom: 100, left: 450, right: 100 },
                  maxZoom: 19,
                  duration: 1000
                });
              }
            }, 100);
          }
        } catch (boundsError) {
          // Error fitting map bounds
        }
      }
    } catch (err) {
      // Reverse geocode error
      setRvgLoading(false);
      setRvgResults([]);
      setShowSidebar(false);
    }
  }, [buildInverseMaskFeature, mapRef, showSidebar]);

  // Clear all RVG markers
  const clearRvgMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      if (marker) marker.remove();
    });
    markersRef.current = [];
    if (popupMarkerRef.current) {
      popupMarkerRef.current.remove();
      popupMarkerRef.current = null;
    }
  }, []);

  // Clear RVG polygon overlay
  const clearRvgPolygon = useCallback(() => {
    if (!mapRef.current) return;

    try {
      // Remove polygon layers
      if (mapRef.current.getLayer && mapRef.current.getLayer('rvg-shape-fill')) {
        mapRef.current.removeLayer('rvg-shape-fill');
      }
      if (mapRef.current.getLayer && mapRef.current.getLayer('rvg-shape-outside')) {
        mapRef.current.removeLayer('rvg-shape-outside');
      }
      if (mapRef.current.getLayer && mapRef.current.getLayer('rvg-shape-outline')) {
        mapRef.current.removeLayer('rvg-shape-outline');
      }
      // Remove polygon source
      if (mapRef.current.getSource && mapRef.current.getSource('rvg-shape')) {
        mapRef.current.removeSource('rvg-shape');
      }
      if (mapRef.current.getSource && mapRef.current.getSource('rvg-shape-mask')) {
        mapRef.current.removeSource('rvg-shape-mask');
      }
    } catch (error) {
      // Error clearing RVG polygon
    }
  }, [mapRef]);

  return {
    rvgResults,
    setRvgResults,
    showSidebar,
    setShowSidebar,
    isClosing,
    setIsClosing,
    clickedPoint,
    setClickedPoint,
    rvgLoading,
    setRvgLoading,
    highlightedResultIndex,
    setHighlightedResultIndex,
    showPopup,
    setShowPopup,
    popupData,
    setPopupData,
    popupPosition,
    setPopupPosition,
    markersRef,
    popupMarkerRef,
    callReverseGeocode,
    clearRvgMarkers,
    clearRvgPolygon,
  };
};

